import './recon-v2-theme.css'
// CSS-var twin of the token values below, consumed as `var(--rv2-*)` by the
// feature's .module.scss stylesheets. Side-effect import → globally available.
import './recon-v2-tokens.scss'

// SureCo Design System (teal) — token set for Payment Reconciliation V2.
// Mirrors the design's tokens.css + colors_and_type.css. Values are hex so
// styled-components can consume them directly; fonts load via recon-v2-theme.css.

export const font = {
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  display: "'IBM Plex Serif', 'Source Serif Pro', Georgia, serif",
  mono: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
} as const

export const colors = {
  brand: {
    primary: '#01A0A8',
    primaryHover: '#0A939B',
    primaryActive: '#004D4D',
    soft: '#E5F7F5',
  },
  bg: {
    page: '#FFFFFF',
    surface: '#FFFFFF',
    raised: '#FAFBFC',
    sunken: '#F3F6F9',
    hover: '#F4F7FA',
    header: '#F8F9FB',
    grey50: '#F6F7FA',
  },
  border: {
    subtle: '#EDEFF1',
    default: '#D9E2EC',
    strong: '#9FB3C8',
    input: '#CBD2D9',
    focus: '#0A939B',
  },
  text: {
    primary: '#112A43',
    secondary: '#334E68',
    muted: '#617D99',
    subtext: '#617D99',
    placeholder: '#839AB1',
    faint: '#9AA5B1',
    disabled: '#9FB3C8',
    inverse: '#FFFFFF',
  },
  status: {
    paid: { bg: 'transparent', fg: '#112A43', border: 'transparent' },
    underpaid: {
      bg: '#FCEADF',
      fg: '#B23F41',
      border: '#F1C7B4',
      hover: '#FAD3C0',
    },
    overpaid: {
      bg: '#FCEADF',
      fg: '#B23F41',
      border: '#F1C7B4',
      hover: '#FAD3C0',
    },
    unpaid: { bg: 'transparent', fg: '#839AB1', border: 'transparent' },
    unknown: { bg: 'transparent', fg: '#617D99', border: 'transparent' },
    pastEnd: {
      bg: 'repeating-linear-gradient(135deg,#DDE1E5 0px,#DDE1E5 1px,transparent 1px,transparent 8px)',
      bgColor: '#FBFBFC',
      fg: '#9FB3C8',
      border: 'transparent',
    },
  },
  accent: {
    primary: '#01A0A8',
    primarySoft: '#E5F7F5',
    warning: '#C9952A',
    warningSoft: '#FBF3DF',
    warningBorder: '#ECD9A6',
    warningText: '#6B4E16',
    danger: '#B23F41',
    blue: '#2D5F9E',
    blueSoft: '#EAF3FB',
    blueSoftAlt: '#E7F0FA',
  },
  provider: {
    marqeta: { bg: '#F4EFFB', border: '#C9B8EC', fg: '#6B4BA8' },
    wex: { bg: '#EEF5FC', border: '#B9D4EE', fg: '#2D5F9E' },
    eePaid: { bg: '#DCF1F2', fg: '#0A939B' },
    refund: { bg: '#FCEADF', fg: '#B23F41' },
  },
  chip: {
    currentBorder: '#2D5F9E',
    selectedBorder: '#01A0A8',
    selectedBg: '#E5F7F5',
    idleBorder: '#D9E2EC',
    outCovBorder: '#E8EBEE',
  },
  equation: {
    bg: '#F5FADF',
    over: '#B23F41',
    exact: '#879E4B',
    remaining: '#01A0A8',
  },
  toast: { bg: '#1F2E3D', fg: '#FFFFFF', icon: '#9FE08A' },
  grey: {
    50: '#F6F7FA',
    100: '#E5E7EB',
    200: '#CCD2D9',
    300: '#9AA5B1',
    400: '#7B8794',
    500: '#626E7C',
    600: '#52606D',
    700: '#323F4B',
    900: '#1F2933',
  },
} as const

export const space = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const

export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 10,
  pill: 9999,
} as const

export const typography = {
  mono: {
    fontFamily: font.mono,
    fontFeatureSettings: '"tnum"',
  },
  // Disables Antd v5 global tnum where it produces "$456 . 49" spacing.
  numNormal: {
    fontFeatureSettings: 'normal',
    letterSpacing: '-0.1px',
  },
  family: font,
  size: {
    xs: 10,
    sm: 11,
    base: 12,
    md: 13,
    lg: 14,
    xl: 16,
    display: 29,
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const
